import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/carousel";
import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ScrollArea } from "~/components/ui/scroll-area";
import { X } from "lucide-react";
import { IMAGE_SIZES, buildImageUrl } from "~/lib/utils";

export default function ImageGalleryPopUp({
  open,
  onOpenChange,
  images,
  sections = [{ id: -1, label: "Alle Bilder" }],
  activeImageId,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  images: {
    id: number | null;
    path: string;
    mimetype: string | null;
    alt: string | null;
    size: number | null;
    sort: number | null;
    category: string | null;
    categoryId: number | null;
  }[];
  sections?: { id: number; label: string }[];
  activeImageId?: string | number;
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeImage, setActiveImage] = useState<number | null>(() => {
    const initial =
      activeImageId != null ? Number(activeImageId) : images[0]?.id ?? null;
    return initial != null ? initial : null;
  });
  const [activeTab, setActiveTab] = useState(sections[0].id);

  const filteredImages = useRef(images);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const mobileThumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleCategoryChange = (id: number) => {
    setActiveTab(id);

    filteredImages.current =
      id === -1 ? images : images.filter((img) => img.categoryId === id);

    filteredImages.current.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    setActiveImage(filteredImages.current[0].id);
  };

  const handleCarouselSelect = useCallback(() => {
    if (!carouselApi) return;
    setActiveImage(
      filteredImages.current[carouselApi.selectedScrollSnap()].id
    );
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;

    handleCarouselSelect();
    carouselApi.on("select", handleCarouselSelect);

    return () => {
      carouselApi.off("select", handleCarouselSelect);
    };
  }, [carouselApi, handleCarouselSelect]);

  useEffect(() => {
    const next =
      activeImageId != null ? Number(activeImageId) : images[0]?.id ?? null;
    setActiveImage(next != null ? next : null);
  }, [activeImageId, images]);

  useEffect(() => {
    const id = activeImage != null ? Number(activeImage) : null;
    if (id == null) return;
    const index = filteredImages.current.findIndex((img) => img.id === id);
    if (index >= 0) {
      thumbnailRefs.current[index]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
      mobileThumbnailRefs.current[index]?.scrollIntoView({
        inline: "center",
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeImage]);

  const startIndex = (() => {
    const targetId =
      activeImage ??
      (activeImageId != null ? Number(activeImageId) : images[0]?.id ?? null);
    if (targetId == null) return 0;
    const idx = filteredImages.current.findIndex((img) => img.id === targetId);
    return idx >= 0 ? idx : 0;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle className="sr-only">Alle Bilder</DialogTitle>
      <DialogDescription className="sr-only">Bildergalerie</DialogDescription>

      <DialogContent
        className="sm:max-w-[80vw] max-h-[90vh] p-0 border-0 rounded-lg min-w-0 overflow-hidden"
        overlayIntensity="dark"
        showCloseButton={false}
      >
        {/* ═══════ Desktop layout ═══════ */}
        <div className="hidden md:block">
          <div className="w-full bg-white z-40 border-b-2 rounded-t-lg px-8 mb-4 relative">
            <div className="flex gap-5 py-4 px-5">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleCategoryChange(s.id)}
                  className="relative group cursor-pointer px-2"
                >
                  {s.label}
                  {activeTab === s.id ? (
                    <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full"></span>
                  ) : (
                    <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full scale-x-0 group-hover:scale-x-100"></span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-8 top-1/2 -translate-y-1/2 text-dove-gray hover:text-[#060606] hover:cursor-pointer"
            >
              <X />
            </button>
          </div>
          <div className="max-h-[calc(90vh-64px-52px-32px)]">
            {filteredImages.current.length > 0 && (
              <div className="flex gap-4">
                <ScrollArea className="h-[calc(90vh-64px-52px-32px)] flex flex-col">
                  <div className="flex flex-col space-y-2 pl-6 pr-4 py-1">
                    {filteredImages.current.map((img, i) => (
                      <button
                        key={i}
                        ref={(el) => {
                          thumbnailRefs.current[i] = el;
                        }}
                        onClick={() => {
                          if (img.id == null) return;
                          setActiveImage(img.id);
                          carouselApi?.scrollTo(i);
                        }}
                        className={clsx(
                          "rounded ring-blue-500 min-w-[200px] min-h-[200px] cursor-pointer",
                          activeImage != null && img.id === activeImage
                            ? "ring-dove-gray ring-offset-2 ring-2"
                            : "",
                        )}
                      >
                        <img
                          src={buildImageUrl(img.path, {
                            size: IMAGE_SIZES.galleryThumb,
                          })}
                          alt=""
                          className="w-[200px] rounded h-[200px] object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </ScrollArea>

                <div className="flex-1 relative">
                  <Carousel
                    setApi={setCarouselApi}
                    opts={{ startIndex }}
                  >
                    <div className="w-full flex justify-center">
                      <div className="w-[80%]">
                        <CarouselContent>
                          {filteredImages.current.map((img, i) => (
                            <CarouselItem
                              key={i}
                              className="h-[calc(90vh-64px-52px-32px)] p-2 flex justify-center rounded w-full"
                            >
                              <img
                                src={buildImageUrl(img.path, {
                                  size: IMAGE_SIZES.galleryLarge,
                                })}
                                alt=""
                                className="object-contain rounded"
                              />
                            </CarouselItem>
                          ))}
                        </CarouselContent>
                      </div>
                    </div>

                    <CarouselPrevious
                      variant="flatWhite"
                      className="rounded shadow-[0_4px_10px_0_rgba(0,0,0,0.25)] absolute left-2 top-1/2 -translate-y-1/2"
                    />
                    <CarouselNext
                      variant="flatWhite"
                      className="rounded shadow-[0_4px_10px_0_rgba(0,0,0,0.25)] absolute right-2 top-1/2 -translate-y-1/2"
                    />
                  </Carousel>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════ Mobile layout ═══════ */}
        <div className="contents md:hidden">
          {/* Header: tabs + close */}
          <div
            className="shrink-0 min-w-0 flex items-center bg-white border-b z-10"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            <div className="flex-1 min-w-0 overflow-x-auto scrollbar-none">
              <div className="flex items-center gap-1 px-4 py-3">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleCategoryChange(s.id)}
                    className={clsx(
                      "relative whitespace-nowrap px-3 py-1.5 text-sm font-medium rounded-full transition-colors cursor-pointer",
                      activeTab === s.id
                        ? "bg-neutral-900 text-white"
                        : "text-neutral-600 hover:bg-neutral-100"
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="shrink-0 p-3 mr-1 text-neutral-500 hover:text-neutral-900 cursor-pointer"
              aria-label="Schließen"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Main carousel */}
          {filteredImages.current.length > 0 && (
            <div className="relative bg-black flex-1 min-h-0 min-w-0 overflow-hidden">
              <Carousel
                setApi={setCarouselApi}
                opts={{ startIndex }}
                className="h-full w-full *:data-[slot=carousel-content]:h-full"
              >
                <CarouselContent className="h-full">
                  {filteredImages.current.map((img, i) => (
                    <CarouselItem
                      key={i}
                      className="h-full flex items-center justify-center px-4 py-3"
                    >
                      <img
                        src={buildImageUrl(img.path, {
                          size: IMAGE_SIZES.galleryLarge,
                        })}
                        alt=""
                        className="max-w-full max-h-full object-contain"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>

                <CarouselPrevious
                  variant="flatWhite"
                  className="rounded shadow-md absolute left-2 top-1/2 -translate-y-1/2 size-8"
                />
                <CarouselNext
                  variant="flatWhite"
                  className="rounded shadow-md absolute right-2 top-1/2 -translate-y-1/2 size-8"
                />
              </Carousel>

              {filteredImages.current.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
                  {(() => {
                    const idx = filteredImages.current.findIndex(
                      (img) => img.id === activeImage
                    );
                    return `${idx + 1} / ${filteredImages.current.length}`;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Thumbnail strip */}
          {filteredImages.current.length > 1 && (
            <div
              className="shrink-0 min-w-0 overflow-hidden bg-white border-t"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              <div className="flex gap-2 overflow-x-auto scrollbar-none px-3 py-3 items-center">
                {filteredImages.current.map((img, i) => (
                  <button
                    key={i}
                    ref={(el) => {
                      mobileThumbnailRefs.current[i] = el;
                    }}
                    onClick={() => {
                      setActiveImage(img.id);
                      carouselApi?.scrollTo(i);
                    }}
                    className={clsx(
                      "shrink-0 rounded-md overflow-hidden cursor-pointer transition-all",
                      activeImage != null && img.id === activeImage
                        ? "ring-2 ring-neutral-900 ring-offset-1"
                        : "opacity-60",
                    )}
                  >
                    <img
                      src={buildImageUrl(img.path, {
                        size: IMAGE_SIZES.galleryThumb,
                      })}
                      alt=""
                      className="w-16 h-16 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
